import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

// ============================================================================
// Helpers
// ============================================================================

export function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) {
    // Normalize to "9:00 am" style used on the contact-page example.
    return trimmed.replace(/\s*(am|pm)\s*$/i, (_, mer) => ` ${mer.toLowerCase()}`);
  }
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'pm' : 'am';
  const hr = h % 12 || 12;
  const mins = isNaN(m) ? 0 : m;
  return `${hr}:${String(mins).padStart(2, '0')} ${suffix}`;
}

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

/**
 * Collapses consecutive days that share the same open/close times into a
 * single range row, e.g. Mon – Fri: 9:00 am – 6:00 pm. Walks every day of the
 * week so days without provided hours (e.g. Sunday) render as "Closed" rather
 * than being omitted.
 */
export function condenseOfficeHours(hours: OfficeHourEntry[]): CondensedHourRow[] {
  // Index provided entries by canonical day key so we can look each day up
  // while walking the full week below.
  const byDay = new Map<string, OfficeHourEntry>();
  for (const entry of hours) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const rows: CondensedHourRow[] = [];

  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const timeLabel = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time)} – ${formatTime(entry.close_time)}`;

    const prev = rows[rows.length - 1];
    if (prev && prev.timeLabel === timeLabel) {
      // Extend the previous group's range to include this day.
      const dash = prev.dayLabel.indexOf(' – ');
      const startLabel = dash !== -1 ? prev.dayLabel.slice(0, dash) : prev.dayLabel;
      prev.dayLabel = `${startLabel} – ${dayLabel}`;
    } else {
      rows.push({ dayLabel, timeLabel });
    }
  }

  return rows;
}

// ============================================================================
// Component
// ============================================================================

interface PropertyInfoPanelProps {
  /** Token prefix of the parent component, e.g. "contactform" or "ahscheduler" */
  prefix: string;
  /**
   * Slot group definition for the address. The parent passes its own literal
   * (the one referenced by the parent's contentSchema) so the editor sees the
   * same definition at the runtime wrapper and the schema-lint anchor.
   */
  addressDefinition: SlotGroupDefinition;
}

/**
 * Shared info panel — Visit Us, Call Us, Office Hours cards.
 * Not a registered slot; imported directly by the parent section. The parent
 * component owns the contentSchema and provides lint anchors.
 *
 * Copy-source of @vibesites/theme-theme-homestead's PropertyInfoPanel (which is
 * not re-exported from the package entry, so it is vendored here).
 */
export function PropertyInfoPanel({ prefix, addressDefinition }: PropertyInfoPanelProps) {
  const visitLabelPath  = `${prefix}.visit_label`;
  const callLabelPath   = `${prefix}.call_label`;
  const hoursLabelPath  = `${prefix}.hours_label`;
  const addressPath     = `${prefix}.address`;
  const phonePath       = `${prefix}.phone`;
  const officeHoursPath = `${prefix}.office_hours`;

  const visitLabel  = useContentToken(visitLabelPath);
  const callLabel   = useContentToken(callLabelPath);
  const hoursLabel  = useContentToken(hoursLabelPath);
  const address     = useSlotGroup(addressPath, addressDefinition);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';
  const phone       = useContentToken(phonePath) || '';

  const officeHours = useCollectionBinding<OfficeHourEntry>(officeHoursPath, {
    collection: 'office_hours',
  });

  const hasPhone = !!phone;
  const hasHours = officeHours.length > 0;
  const phoneDisplay = hasPhone ? formatDisplayPhone(phone) : '(555) 555-5555';

  return (
    <>
      <div className="cf-info-cards">
        <div className="cf-card">
          <EditableToken path={callLabelPath} as="h3" className="cf-card-label">
            {callLabel || 'Call Us'}
          </EditableToken>
          {hasPhone ? (
            <a
              href={`tel:${phone}`}
              className="dniphone dniphonehref cf-card-value cf-card-link"
            >
              <EditableToken path={phonePath} as="span">
                {phoneDisplay}
              </EditableToken>
            </a>
          ) : (
            <EditableToken path={phonePath} as="p" className="cf-card-value">
              {phoneDisplay}
            </EditableToken>
          )}
        </div>

        {/* Visit Us — always rendered; property_full_address should always have a value */}
        <div className="cf-card">
          <EditableToken path={visitLabelPath} as="h3" className="cf-card-label">
            {visitLabel || 'Visit Us'}
          </EditableToken>
          <EditableSlotGroup groupPath={addressPath} definition={addressDefinition}>
            {addressHref ? (
              <a
                href={addressHref}
                target={address._target || '_blank'}
                rel="noopener noreferrer"
                className="cf-card-value cf-card-link"
              >
                {address.text || '123 Main Street, City, ST 00000'}
              </a>
            ) : (
              <p className="cf-card-value">
                {address.text || '123 Main Street, City, ST 00000'}
              </p>
            )}
          </EditableSlotGroup>
        </div>

        {hasHours && (
          <div className="cf-card cf-card--hours">
            <EditableToken path={hoursLabelPath} as="h3" className="cf-card-label">
              {hoursLabel || 'Office Hours'}
            </EditableToken>
            <div className="cf-hours-grid">
              {condenseOfficeHours(officeHours).map((row, i) => (
                <div key={i} className="cf-hours-row">
                  <span className="cf-hours-day">{row.dayLabel}</span>
                  <span className="cf-hours-time">{row.timeLabel}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ===== Contact info — flat text blocks on the section background ===== */
        .cf-info-cards {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.75rem;
          width: 100%;
        }
        .cf-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 0.35rem;
          width: 100%;
          max-width: 22rem;
          padding: 0;
          background: transparent;
          border-radius: 0;
          box-shadow: none;
        }
        .cf-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .cf-card-link:hover { color: var(--vibe-color-primary); }

        /* Compact two-column hours: shared day/time columns, block centered */
        .cf-hours-grid {
          display: grid;
          grid-template-columns: max-content max-content;
          column-gap: 1.75rem;
          row-gap: 0.15rem;
          width: max-content;
          max-width: 100%;
          margin: 0.15rem auto 0;
          justify-content: center;
        }
        .cf-hours-row {
          display: contents;
        }
        .cf-hours-day,
        .cf-hours-time {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.55;
          color: var(--vibe-color-text);
          text-align: left;
          white-space: nowrap;
        }
      `}</style>
    </>
  );
}
